<template>
  <div>
    <div v-if="isMobileOrTablet" class="mobile-drawer-page">

      <!-- ── Sticky top bar with title + close ── -->
      <div class="mobile-drawer-topbar">
        <span class="mobile-drawer-topbar__title">Categories</span>
        <button class="mobile-drawer-close" aria-label="Close" @click="closeDrawer()">
          ✕
        </button>
      </div>

      <!-- ── Scrollable category list ── -->
      <div class="mobile-drawer-body">

        <!-- Services section -->
        <div v-if="exclusiveCategories && exclusiveCategories.length > 0" class="mobile-drawer-section">
          <p class="mobile-drawer-section__label">Services</p>
          <ul class="mobile-drawer-list">
            <li v-if="isTravelConciergeEnabled">
              <router-link :to="{ name: 'TravelEvents' }" class="mobile-drawer-item mobile-drawer-item--service">
                <font-awesome-icon icon="plane" transform="shrink-2" />
                Travel &amp; events
              </router-link>
            </li>
            <li v-if="isCustomItemEnabled">
              <router-link :to="{ name: 'CustomItem' }" class="mobile-drawer-item mobile-drawer-item--service">
                <font-awesome-icon icon="box" transform="shrink-2" />
                Custom items
              </router-link>
            </li>
            <li v-if="isTicketAndExperiencesEnabled">
              <router-link :to="{ name: 'TicketAndExperiences' }" class="mobile-drawer-item mobile-drawer-item--service">
                <font-awesome-icon icon="box" transform="shrink-2" />
                Tickets &amp; Experiences
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Divider -->
        <div v-if="exclusiveCategories && exclusiveCategories.length > 0" class="mobile-drawer-divider"></div>

        <!-- Categories section -->
        <div class="mobile-drawer-section">
          <p class="mobile-drawer-section__label">Categories</p>
          <ul class="mobile-drawer-list">
            <li v-for="category in parentCategories" :key="category.id">
              <button
                v-on:click="loadSubcategory(category.id)"
                class="mobile-drawer-item mobile-drawer-item--parent"
                :class="{ 'mobile-drawer-item--active': searchId === category.id && category.id !== 1 }"
                :disabled="parentCategories.length <= 1"
              >
                <span>{{ category.id === 1 ? "← Back" : category.name }}</span>
                <span class="mobile-drawer-item__chevron" v-if="category.id !== 1">›</span>
              </button>
            </li>
            <li v-for="category in childrenCategories" :key="category.id">
              <button
                v-on:click="loadSubcategory(category.id)"
                class="mobile-drawer-item mobile-drawer-item--child"
                :class="{ 'mobile-drawer-item--active': searchId === category.id }"
              >
                <span>{{ category.name }}</span>
                <span class="mobile-drawer-item__chevron">›</span>
              </button>
            </li>
          </ul>
        </div>
      </div>

      <!-- ── Sticky bottom action bar ── -->
      <div class="mobile-drawer-footer">
        <button
          class="mobile-drawer-btn mobile-drawer-btn--reset"
          :disabled="resetDisabled"
          v-on:click="resetFilter()"
        >
          Reset
        </button>
        <div class="mobile-drawer-btn-link">
          <button
            class="mobile-drawer-btn mobile-drawer-btn--apply"
            @click="applyAndClose()"
          >Apply</button>
        </div>
      </div>

    </div>

    <!-- ===== DESKTOP SIDEBAR ===== -->
    <div v-else>
      <div class="shop-sidebar">
        <div v-if="exclusiveCategories && exclusiveCategories.length > 0" class="sidebar-section">
          <h5 class="sidebar-heading">Services</h5>
          <ul class="services">
            <li v-if="isTravelConciergeEnabled">
              <router-link :to="{ name: 'TravelEvents' }">
                <font-awesome-icon icon="plane" transform="shrink-2" />
                Travel &amp; events
              </router-link>
            </li>
            <li v-if="isCustomItemEnabled">
              <router-link :to="{ name: 'CustomItem' }">
                <font-awesome-icon icon="box" transform="shrink-2" />
                Custom items
              </router-link>
            </li>
            <li v-if="isTicketAndExperiencesEnabled">
              <router-link :to="{ name: 'TicketAndExperiences' }">
                <font-awesome-icon icon="box" transform="shrink-2" />
                Tickets &amp; Experiences
              </router-link>
            </li>
          </ul>
        </div>

        <div class="sidebar-section">
          <h5 class="sidebar-heading">Categories</h5>
          <ul class="categories">
            <li
              v-for="category in parentCategories"
              :key="category.id"
              class="parent"
            >
              <router-link
                :to="{ name: 'Category', params: { categoryId: category.id } }"
                aria-expanded="false"
                aria-haspopup="true"
              >
                <strong>{{ category.name }}</strong>
              </router-link>
            </li>
            <li
              v-for="category in childrenCategories"
              :key="category.id"
              class="children"
            >
              <router-link
                :to="{ name: 'Category', params: { categoryId: category.id } }"
                aria-expanded="false"
                aria-haspopup="true"
              >
                {{ category.name }}
              </router-link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Header from "@/components/Header.vue";
import navigationFooter from "../../components/navigationFooter.vue";
import { isMobile, isTablet } from "mobile-device-detect";

export default {
  data() {
    return {
      searchId: 1,
      resetDisabled: true,
    };
  },
  beforeMount() {
    if (this.isMobileOrTablet) {
      // Sync searchId from store so re-opening highlights the active category
      // Do NOT dispatch setCurrentCategoryId here — that resets the filter
      const activeCategoryId = this.currentCategoryId || 1;
      this.searchId = activeCategoryId;
      this.resetDisabled = activeCategoryId === 1;
    }
  },
  computed: {
    ...mapState({
      currentCategoryId: (state) => state.category.currentCategoryId,
      parentCategories: (state) => state.category.parents,
      childrenCategories: (state) => state.category.children,
      exclusiveCategories: (state) => state.category.exclusives,
    }),
    isMobileOrTablet: function () { return isMobile || isTablet; },
    isTravelConciergeEnabled: function () {
      return this.exclusiveCategories.filter((item) => item["type"] === "CONCIERGE").length > 0;
    },
    isCustomItemEnabled: function () {
      return this.exclusiveCategories.filter((item) => item["type"] === "CUSTOM_ITEM").length > 0;
    },
    isTicketAndExperiencesEnabled: function () {
      return this.exclusiveCategories.filter((item) => item["type"] === "TICKETSANDEXPERIENCES").length > 0;
    },
  },
  components: {
    appHeader: Header,
    navigationFooter: navigationFooter,
  },
  methods: {
    loadSubcategory(Id) {
      this.$store.dispatch("category/setCurrentCategoryId", Id);
      this.searchId = Id;
      this.resetDisabled = false;
    },
    resetFilter() {
      this.$store.dispatch("category/setCurrentCategoryId", 1);
      this.searchId = 1;
      this.resetDisabled = true;
    },
    applyAndClose() {
      this.$router.push({ name: "Category", params: { categoryId: this.searchId } });
    },
    closeDrawer() {
      const categoryId = this.currentCategoryId || 1;
      this.$router.push({ name: "Category", params: { categoryId: categoryId } });
    },
  },
  created() {
    if (!this.currentCategoryId) {
      this.$store.dispatch("category/fetchRootCategory");
    }
    this.$store.dispatch("category/fetchExclusives");
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════
   MOBILE FULL-SCREEN DRAWER
═══════════════════════════════════════════════ */
.mobile-drawer-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #f7f8fc;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
}

/* ── Top bar ── */
.mobile-drawer-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #eaecf4;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}

.mobile-drawer-topbar__title {
  font-size: 17px;
  font-weight: 700;
  color: #2d3142;
  letter-spacing: -0.2px;
}

.mobile-drawer-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f1f5;
  border-radius: 50%;
  font-size: 16px;
  color: #555;
  text-decoration: none;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.mobile-drawer-close:hover {
  background: #e0e3f0;
  color: #2d3142;
}

/* ── Scrollable body ── */
.mobile-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 0 16px;
  -webkit-overflow-scrolling: touch;
}

/* ── Section ── */
.mobile-drawer-section {
  padding: 0 16px;
  margin-bottom: 8px;
}

.mobile-drawer-section__label {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.9px;
  color: #aab0c6;
  margin: 0 0 6px 4px;
}

.mobile-drawer-divider {
  height: 1px;
  background: #eaecf4;
  margin: 8px 16px 14px;
}

/* ── List ── */
.mobile-drawer-list {
  list-style: none;
  padding: 0;
  margin: 0;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #eaecf4;
}

.mobile-drawer-list li + li {
  border-top: 1px solid #f0f1f5;
}

/* ── Row item ── */
.mobile-drawer-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  background: #fff;
  border: none;
  text-align: left;
  font-size: 14px;
  color: #2d3142;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
}

.mobile-drawer-item:hover,
.mobile-drawer-item:active {
  background: #f4f5ff;
  text-decoration: none;
  color: #4f6ef7;
}

.mobile-drawer-item--parent {
  font-weight: 700;
  font-size: 14.5px;
}

.mobile-drawer-item--parent:disabled {
  color: #bbb;
  cursor: default;
  background: #fff;
}

.mobile-drawer-item--child {
  font-weight: 400;
  color: #4a4f6a;
  padding-left: 24px;
}

.mobile-drawer-item--service {
  font-weight: 500;
  color: #4a4f6a;
  gap: 10px;
  justify-content: flex-start;
}

/* Fix 2: active category highlight */
.mobile-drawer-item--active {
  background: #eff2ff !important;
  color: #4f6ef7 !important;
  font-weight: 700 !important;
  border-left: 3px solid #4f6ef7;
  padding-left: 13px;
}

.mobile-drawer-item__chevron {
  color: #bbb;
  font-size: 18px;
  font-weight: 300;
  line-height: 1;
}

/* ── Footer action bar ── */
.mobile-drawer-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px 24px;
  background: #fff;
  border-top: 1px solid #eaecf4;
  flex-shrink: 0;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.06);
  /* Ensure visible above device safe area / bottom bars */
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 16px));
}

.mobile-drawer-btn-link {
  flex: 1;
  text-decoration: none;
}

.mobile-drawer-btn {
  width: 100%;
  height: 48px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.mobile-drawer-btn--reset {
  background: #f0f1f5;
  color: #666;
  flex: 0 0 100px;
  width: 100px;
  border: 1.5px solid #dde1eb;
}

.mobile-drawer-btn--reset:disabled {
  color: #ccc;
  cursor: not-allowed;
  border-color: #eee;
}

.mobile-drawer-btn--reset:not(:disabled):hover {
  background: #e5e7ef;
  color: #2d3142;
}

.mobile-drawer-btn--apply {
  background: linear-gradient(135deg, #4f6ef7, #6c85fa);
  color: #fff;
  box-shadow: 0 3px 12px rgba(79,110,247,0.35);
}

.mobile-drawer-btn--apply:hover {
  opacity: 0.92;
  box-shadow: 0 4px 16px rgba(79,110,247,0.45);
}
</style>

<style lang="scss">
.h5::after { content: none; }
</style>

<style lang="scss" scoped>
%underlined-item {
  content: " ";
  width: 100%;
  position: absolute;
  height: 2px;
  border-radius: 10px;
  background-color: $primary;
  display: block;
  border-radius: 5px;
  margin-top: 0.1em;
}

.shop-sidebar {
  background: #fff;
  border: 1px solid #eaecf4;
  border-radius: 14px;
  padding: 20px 18px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.05);

  ul {
    list-style: none;
    padding-left: 0;
    margin: 0;
  }

  li {
    margin: 0;
    svg { margin-right: 8px; }
  }

  .sidebar-section {
    margin-bottom: 24px;
    &:last-child { margin-bottom: 0; }
  }

  a {
    color: #4a4f6a;
    font-weight: 400;
    font-size: 14px;
    display: flex;
    align-items: center;
    padding: 7px 10px;
    border-radius: 8px;
    text-decoration: none;
    transition: background 0.18s, color 0.18s;

    &:hover {
      background: #f0f3ff;
      color: #4f6ef7;
      text-decoration: none;
    }
  }

  .router-link-active {
    background: #eff2ff;
    color: #4f6ef7 !important;
    font-weight: 700;
    position: relative;
  }

  .categories {
    .parent {
      > a strong {
        font-size: 13.5px;
        color: #2d3142;
        letter-spacing: 0.2px;
      }
    }
    .children {
      margin-left: 12px;
      a { font-size: 13px; }
    }
  }

  .services {
    li a {
      color: #4a4f6a;
    }
  }
}

/* ─── SIDEBAR HEADING ─────────────────────────────────── */
.sidebar-heading {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #aab0c6;
  margin: 0 0 10px;
  padding: 0 4px;
}
</style>